import React, { type ChangeEvent, useCallback, useMemo } from 'react';
import type { CouponInputProps } from './coupon-input.types';
import Text from '../../atoms/text';
import Button from '../../atoms/button';
import { TextInput } from '../../molecules/text-input';
import { TicketGrayIcon } from '../../atoms/icons';
import styles from './coupon-input.module.css';
import { getCouponStyles } from './coupon-input.styles';
import Icon from '../../atoms/icon';
import { X } from 'lucide-react';
import { getColorVar } from '../../../theme';
import { CouponInputTestId } from './coupon-input.constants';
import { cn } from '../../utils/cn';

export const CouponInput: React.FC<CouponInputProps> = ({
    className,
    contentClassName,
    inputClassName,
    titleClassName,
    buttonLabel,
    couponCode,
    couponText,
    errorText,
    inputPlaceholder,
    isInputButton,
    variant = 'compact',
    isVisibleRightButton = true,
    textValue: inputValue,
    onPressCoupon,
    onKeyDownEnter,
    onChangeText,
    textInputProps,
    textProps,
    titleProps,
    isDisabled,
    hasIconRight = false,
    label = 'Mã giảm giá',
}) => {
    const isCouponApplied = !!couponCode;
    const isShowTextInput = useMemo(() => {
        if (couponText) return false;

        if (variant === 'compact') {
            return !isCouponApplied;
        }

        return true;
    }, [couponText, variant, isCouponApplied]);

    const onPressButtonContainer = useCallback(() => {
        if (!onPressCoupon) return;
        onPressCoupon();
    }, [onPressCoupon]);

    const onClearText = useCallback(() => {
        onChangeText?.('');
    }, [onChangeText]);

    const onChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const value = event?.target?.value;
            onChangeText?.(value);
        },
        [onChangeText]
    );

    const renderRightIcon = useCallback(() => {
        return <Icon containerClassName="h-full" icon={X} size={'small'} color={getColorVar('slate', '20')} />;
    }, []);

    const appliedCouponCodeLabel = useMemo(() => {
        if (!isCouponApplied) {
            return buttonLabel;
        }

        return couponCode ?? '';
    }, [isCouponApplied, couponCode, buttonLabel]);

    const defaultRightButtonLabel = useMemo(() => {
        return buttonLabel;
    }, [buttonLabel]);

    const leftIconClassName = useMemo(
        () => (!isDisabled && hasIconRight ? styles.rightInputIconShow : styles.rightInputIconHide),
        [isDisabled, hasIconRight]
    );

    const couponStyle = useMemo(() => getCouponStyles(variant, isCouponApplied), [variant, isCouponApplied]);
    const onKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                onKeyDownEnter?.();
            }
        },
        [onKeyDownEnter]
    );

    return (
        <div className={cn(styles.container, className)}>
            <div className={cn(styles.contentContainer, contentClassName)}>
                <div className={styles.imageIcon}>
                    {isCouponApplied ? (
                        <Text className={cn(styles.couponTitle, titleClassName)} {...titleProps}>
                            {label}
                        </Text>
                    ) : (
                        <TicketGrayIcon />
                    )}
                </div>

                {isShowTextInput && (
                    <div className={cn(styles.buttonContainer, inputClassName)}>
                        <TextInput
                            className={couponStyle.textInput}
                            testID={CouponInputTestId.INPUT}
                            onChange={onChange}
                            value={couponCode ?? inputValue}
                            onKeyDown={onKeyDown}
                            placeholder={inputPlaceholder}
                            iconRightProps={{
                                className: leftIconClassName,
                                onPress: onClearText,
                                icon: renderRightIcon(),
                            }}
                            disabled={isInputButton}
                            size="small"
                            {...textInputProps}
                        />
                    </div>
                )}

                <div className={styles.rightSection}>
                    {/* Shipping discount badge rendered outside the button when no coupon is applied */}
                    {couponText && (
                        <div className={styles.innerText}>
                            <div className={styles.outerText}>
                                <Text
                                    className={cn(styles.couponText, styles.couponTextWithAppliedCoupon)}
                                    {...textProps}
                                >
                                    {couponText}
                                </Text>
                            </div>
                        </div>
                    )}

                    <Button
                        testID={CouponInputTestId.BUTTON_RIGHT}
                        showIf={isVisibleRightButton}
                        className={couponStyle.rightButton}
                        onPress={onPressButtonContainer}
                        size={'small'}
                    >
                        {isCouponApplied ? (
                            <div className={styles.renderAppliedCoupon}>
                                <div className={styles.innerCode}>
                                    <div className={styles.outerCode}>
                                        <Text className={couponStyle.couponText} {...textProps}>
                                            {appliedCouponCodeLabel}
                                        </Text>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <Text size="large" className={couponStyle.couponText} {...textProps}>
                                {defaultRightButtonLabel}
                            </Text>
                        )}
                    </Button>
                </div>
            </div>
            <Text showIf={!!errorText} className={styles.errorText}>
                {errorText}
            </Text>
        </div>
    );
};
